import { ServiceRegistry } from '@core/ServiceRegistry';
import { BACKEND_GATEWAY_TOKEN, FILE_MANAGER_SERVICE_TOKEN, CONFIG_SERVICE_TOKEN, MACHINE_SERVICE_TOKEN, STATE_SERVICE_TOKEN, EVENT_BUS_TOKEN } from '@core/ServiceTokens';
import { ITransferProtocol } from '../services/transfer/ITransferProtocol';
import { TransferProtocolFactory } from '../services/transfer/TransferProtocolFactory';
import { BackendGateway } from '@services/BackendGateway';
import { IFileManagerService } from '@services/IFileManagerService';
import { IConfigService } from '@services/config/IConfigService';
import { MachineService } from '@services/MachineService';
import { StateService } from '@services/StateService';
import { EventBus, EVENT_NAMES } from '@services/EventBus';

import type { TransferProgram, FileExtensionConfig } from '@core/types';

interface GroupedProgram {
  number: number;
  comment: string;
  paths: {
    1?: TransferProgram;
    2?: TransferProgram;
    3?: TransferProgram;
  };
  isPA: boolean; // Indicates if it exists on multiple paths identically
}

export class NCTransferPanel extends HTMLElement {
  private backend: BackendGateway;
  private transferClient!: ITransferProtocol;
  private fileManager: IFileManagerService;
  private configService: IConfigService;
  private machineService: MachineService;
  private stateService: StateService;
  private eventBus: EventBus;
  private transferProtocol = 'none';

  
  private cncPrograms: Map<number, GroupedProgram> = new Map();
  private ipAddress: string = '192.168.1.1';
  private isConnectedToCnc: boolean = false;
  loading: boolean = false;

  constructor() {
    super();
    this.attachShadow({ mode: 'open' });
    this.backend = ServiceRegistry.getInstance().get(BACKEND_GATEWAY_TOKEN);
    this.fileManager = ServiceRegistry.getInstance().get(FILE_MANAGER_SERVICE_TOKEN);
    this.configService = ServiceRegistry.getInstance().get(CONFIG_SERVICE_TOKEN);
    this.machineService = ServiceRegistry.getInstance().get(MACHINE_SERVICE_TOKEN);
    this.stateService = ServiceRegistry.getInstance().get(STATE_SERVICE_TOKEN);
    this.eventBus = ServiceRegistry.getInstance().get(EVENT_BUS_TOKEN);
    
    // Asynchronously load the default IP address from our configuration factory
    this.configService.getConfig().then(cfg => {
      this.applyTransferConfig(cfg);
      this.render();
      if (this.isConnectedToCnc) {
        this.attachEventListeners();
        void this.checkPing();
      }
    });

    this.configService.onConfigChanged((cfg) => {
      this.applyTransferConfig(cfg);
      this.render();
      this.attachEventListeners();
      if (this.isConnectedToCnc) {
        void this.checkPing();
      }
    });

    // Re-render when machine selection changes (channels / extensions may change)
    this.eventBus.subscribe(EVENT_NAMES.MACHINE_CHANGED, () => {
      this.render();
      this.attachEventListeners();
    });

    // Listen for file drops fetched via Extension
    window.addEventListener('message', (event) => {
        const message = event.data;
        if (message.type === 'DO_TRANSFER_UPLOAD') {
            this.uploadDroppedFile(message.content, message.pathId);
        } else if (message.type === 'USB_DIRECTORY_SELECTED') {
            const ipInput = this.shadowRoot?.getElementById('ip-address') as HTMLInputElement;
            if (ipInput) {
                ipInput.value = message.path;
                this.ipAddress = message.path;
            }
            this.handleConnect();
        }
    });
  }

  connectedCallback() {
    this.render();
    this.attachEventListeners();
    setTimeout(() => this.checkPing(), 100);
  }

  private applyTransferConfig(cfg: Awaited<ReturnType<IConfigService['getConfig']>>) {
    this.transferClient = TransferProtocolFactory.create(cfg.transferProtocol || 'none', this.backend, cfg.transferDriverPath);
    this.transferProtocol = cfg.transferProtocol || 'none';
    this.ipAddress = cfg.transferDefaultIp || (String(this.transferProtocol).toLowerCase() === 'usb' ? '' : '192.168.1.1');
  }

  private async handleConnect() {
    const ipInput = this.shadowRoot?.getElementById('ip-address') as HTMLInputElement;
    if (ipInput) this.ipAddress = ipInput.value;

    try {
      this.loading = true;
      this.render();
      await this.transferClient.connect(this.ipAddress);
      this.isConnectedToCnc = true;
      await this.fetchPrograms();
    } catch (e) {
      alert("Failed to connect to transfer: " + e);
      this.isConnectedToCnc = false;
    } finally {
      this.loading = false;
      this.render();
      this.attachEventListeners();
    }
  }

  private async checkPing() {
    if (String(this.transferProtocol).toLowerCase() === 'usb') return;
    const ipInput = this.shadowRoot?.querySelector('#ip-address') as HTMLInputElement;
    if (!ipInput || !ipInput.value) return;
    
    // Update local state without full reload
    this.ipAddress = ipInput.value;
    
    const pingIndicator = this.shadowRoot?.querySelector('#ping-indicator') as HTMLElement;
    if (pingIndicator) {
      pingIndicator.style.background = 'gray'; // pending
    }
    
    try {
      const available = await this.transferClient.ping(this.ipAddress);
      if (pingIndicator) {
        pingIndicator.style.background = available ? '#89d185' : '#f48771';
        pingIndicator.title = available ? 'Machine Reachable (Ping OK)' : 'Machine Unreachable';
      }
    } catch(e) {
      if (pingIndicator) {
        pingIndicator.style.background = '#f48771';
        pingIndicator.title = 'Ping Failed';
      }
    }
  }

  private getSupportedPaths(): number[] {
    return this.getSupportedChannels();
  }

  // --- Machine file-extension helpers ---

  /** Returns the FileExtensionConfig for the currently selected machine, or undefined.
   * Returns undefined if no machine has been selected yet — callers fall back to
   * sensible defaults (e.g. [1,2,3] for USB). */
  private getCurrentMachineFileExtensions(): FileExtensionConfig | undefined {
    const { globalMachine } = this.stateService.getState();
    if (!globalMachine) return undefined;
    return this.machineService.getMachine(globalMachine)?.fileExtensions;
  }

  /**
   * Returns the sorted channel numbers supported by the current machine.
   * Falls back to [1,2,3] for USB or [1,2] for FOCAS when no machine config is available.
   */
  private getSupportedChannels(): number[] {
    const ext = this.getCurrentMachineFileExtensions();
    if (!ext) {
      return this.transferProtocol === 'usb' ? [1, 2, 3] : [1, 2];
    }
    if (!ext.multifile) {
      // Single-file machine (e.g. Siemens): only one "channel"
      return [1];
    }
    const channels = Object.keys(ext.channels)
      .map(Number)
      .filter(n => !isNaN(n))
      .sort((a, b) => a - b);
    return channels.length > 0 ? channels : [1];
  }

  /** Returns true when the current machine supports PA / multi-channel assembly files. */
  private isMultifileSupported(): boolean {
    const ext = this.getCurrentMachineFileExtensions();
    if (ext === undefined) return true; // default: assume multifile
    return ext.multifile;
  }

  /**
   * Returns the preferred file extension for a given channel number.
   * Uses the first non-empty extension from machine config, falling back to `.P{n}`.
   */
  private getChannelFileExtension(channelNo: number): string {
    const ext = this.getCurrentMachineFileExtensions();
    if (!ext) return `.P${channelNo}`;
    const channelExts = ext.channels[channelNo.toString()];
    if (!channelExts || channelExts.length === 0) return ext.main[0] ?? '';
    // Prefer non-empty extension; "" means no extension (bare filename)
    return channelExts.find(e => e !== '') ?? channelExts[0] ?? '';
  }

  private getChannelFileExtensions(channelNo: number): string[] | undefined {
    return this.getCurrentMachineFileExtensions()?.channels[channelNo.toString()];
  }

  /** Returns the file extension used for PA (multi-channel assembly) programs. */
  private getPAFileExtension(): string {
    const ext = this.getCurrentMachineFileExtensions();
    return ext?.main?.[0] ?? '.PA';
  }

  /**
   * Returns a channel-first label so filename conventions do not obscure the CNC path.
   * e.g. ".M" -> "P1 Main (.M)", ".S" -> "P2 Sub (.S)", ".p-2" -> "P2 (.p-2)".
   */
  private getChannelLabel(channelNo: number): string {
    const extension = this.getChannelFileExtension(channelNo);
    const channelLabel = `P${channelNo}`;
    if (extension.toLowerCase() === `.p${channelNo}`) return channelLabel;

    const config = this.getCurrentMachineFileExtensions();
    const normalizedExtension = extension.toLowerCase();
    const isMain = config?.main.some(item => item.toLowerCase() === normalizedExtension);
    const isSub = config?.subprogram.some(item => item.toLowerCase() === normalizedExtension);
    const role = isMain ? ' Main' : isSub ? ' Sub' : '';
    const extensionLabel = extension || 'no ext';
    return `${channelLabel}${role} (${extensionLabel})`;
  }

  /** Build a save-file name for a channel program, preserving its USB source extension when available. */
  private buildChannelFileName(progNum: number, channelNo: number, sourceExtension?: string): string {
    const ext = sourceExtension ?? this.getChannelFileExtension(channelNo);
    return `O${progNum.toString().padStart(4, '0')}${ext}`;
  }

  /** Build a save-file name for a PA program, e.g. "O0001.PA". */
  private buildPAFileName(progNum: number): string {
    const ext = this.getPAFileExtension();
    return `O${progNum.toString().padStart(4, '0')}${ext}`;
  }

  private async fetchPrograms() {
    const paths = this.getSupportedPaths();
    const multifile = this.isMultifileSupported();
    this.cncPrograms.clear();

    for (const path of paths) {
      try {
        const response = await this.transferClient.listPrograms(this.ipAddress, path, undefined, this.getChannelFileExtensions(path));
        for (const prog of response) {
          if (!this.cncPrograms.has(prog.number)) {
            this.cncPrograms.set(prog.number, {
              number: prog.number,
              comment: prog.comment,
              paths: {},
              isPA: false
            });
          }
          
          const group = this.cncPrograms.get(prog.number)!;
          // @ts-ignore
          group.paths[path as keyof typeof group.paths] = prog;
          
          // A pull is combined when the program exists in multiple channels.
          group.isPA = multifile && Object.keys(group.paths).length > 1;
          if (!group.comment && prog.comment) {
            group.comment = prog.comment;
          }
        }
      } catch (e) {
        console.warn(`Failed to list programs on path ${path}`, e);
      }
    }
  }

  private async handleUpload(progNum: number, pathNo: string) {
    try {
      if (pathNo === 'PA') {
        const prog = this.cncPrograms.get(progNum);
        if (!prog) return;

        let combinedContent = "%\n";
        
        // Include header information (assuming prog.comment is available)
        combinedContent += `&F=/O${progNum.toString().padStart(4, '0')}(${prog.comment || 'PA_PROG'})/\n`;

        for (const p of this.getSupportedPaths()) {
            if (prog.paths[p as 1|2|3]) {
                const resp = await this.transferClient.uploadProgram(this.ipAddress, p, progNum, undefined, this.getChannelFileExtensions(p));
                this.fileManager.updateActiveProgramContent(p.toString(), resp);
                
                // Format block with <> XML-like tags matching the specified standard
                combinedContent += `<O${progNum.toString().padStart(4, '0')}.P${p}>\n`;
                
                // Remove trailing % or whitespace from individual blocks if present, to prevent mid-file terminations
                let cleanText = resp.trim();
                if (cleanText.endsWith('%')) {
                   cleanText = cleanText.slice(0, -1).trimEnd();
                }
                if (cleanText.startsWith('%')) {
                   cleanText = cleanText.slice(1).trimStart();
                }
                // Also strip the `OXXXX` header if it's there
                const firstNewLine = cleanText.indexOf('\n');
                if (cleanText.startsWith('O') && firstNewLine > -1 && firstNewLine < 15) {
                    cleanText = cleanText.slice(firstNewLine + 1).trimStart();
                }
                
                combinedContent += `${cleanText}\n \n`;
            }
        }
        
        combinedContent += "%\n";

        const fileName = this.buildPAFileName(progNum);
        if ((window as any).vscodeApi) {
          (window as any).vscodeApi.postMessage({
                type: 'SAVE_TRANSFER_FILE',
                fileName: fileName,
                content: combinedContent
            });
        }
        alert(`Program O${progNum} (PA) uploaded successfully to workspace!`);

      } else {
        const pNum = parseInt(pathNo, 10);
        const resp = await this.transferClient.uploadProgram(this.ipAddress, pNum, progNum, undefined, this.getChannelFileExtensions(pNum));
        
        // Load into matching local channel
        const channelId = pNum.toString();
        this.fileManager.updateActiveProgramContent(channelId, resp);
        
        const sourceExtension = String(this.transferProtocol).toLowerCase() === 'usb'
          ? this.cncPrograms.get(progNum)?.paths[pNum as 1 | 2 | 3]?.file_extension
          : undefined;
        const fileName = this.buildChannelFileName(progNum, pNum, sourceExtension);
        if ((window as any).vscodeApi) {
          (window as any).vscodeApi.postMessage({
                type: 'SAVE_TRANSFER_FILE',
                fileName: fileName,
                content: resp
            });
        }

        alert(`Program O${progNum} from Path ${pNum} uploaded successfully to workspace!`);
      }
    } catch (e) {
      alert("Upload failed: " + e);
    }
  }

  private async handleCompare(progNum: number, pathNo: string) {
    try {
      if (pathNo === 'PA') {
        const prog = this.cncPrograms.get(progNum);
        if (!prog) return;

        let combinedContent = "%\n";
        
        // Include header information
        combinedContent += `&F=/O${progNum.toString().padStart(4, '0')}(${prog.comment || 'PA_PROG'})/\n`;

        for (const p of this.getSupportedPaths()) {
            if (prog.paths[p as 1|2|3]) {
                const resp = await this.transferClient.uploadProgram(this.ipAddress, p, progNum, undefined, this.getChannelFileExtensions(p));
                
                // Format block with <> XML-like tags matching the specified standard
                combinedContent += `<O${progNum.toString().padStart(4, '0')}.P${p}>\n`;
                
                let cleanText = resp.trim();
                if (cleanText.endsWith('%')) {
                   cleanText = cleanText.slice(0, -1).trimEnd();
                }
                if (cleanText.startsWith('%')) {
                   cleanText = cleanText.slice(1).trimStart();
                }
                // Also strip the `OXXXX` header if it's there
                const firstNewLine = cleanText.indexOf('\n');
                if (cleanText.startsWith('O') && firstNewLine > -1 && firstNewLine < 15) {
                    cleanText = cleanText.slice(firstNewLine + 1).trimStart();
                }
                
                combinedContent += `${cleanText}\n \n`;
            }
        }
        
        combinedContent += "%\n";

        const fileName = this.buildPAFileName(progNum);
        if ((window as any).vscodeApi) {
          (window as any).vscodeApi.postMessage({
                type: 'COMPARE_TRANSFER_FILE',
                fileName: fileName,
                content: combinedContent
            });
        }

      } else {
        const pNum = parseInt(pathNo, 10);
        const resp = await this.transferClient.uploadProgram(this.ipAddress, pNum, progNum, undefined, this.getChannelFileExtensions(pNum));
        
        const fileName = this.buildChannelFileName(progNum, pNum);
        if ((window as any).vscodeApi) {
          (window as any).vscodeApi.postMessage({
                type: 'COMPARE_TRANSFER_FILE',
                fileName: fileName,
                content: resp
            });
        }
      }
    } catch (e) {
      alert("Compare failed: " + e);
    }
  }

  private render() {
    if (!this.shadowRoot) return;

    const supportedPaths = this.getSupportedPaths();
    const multifileSupported = this.isMultifileSupported();
    const isUsbTransfer = String(this.transferProtocol).toLowerCase() === 'usb';
    const locationLabel = isUsbTransfer ? 'USB Storage' : 'CNC Memory';
    const addressPlaceholder = isUsbTransfer ? 'Local folder path (e.g. D:\\)' : 'CNC IP';
    const connectButtonLabel = this.loading ? '...' : (this.isConnectedToCnc ? (isUsbTransfer ? 'Reload' : 'Reconnect') : (isUsbTransfer ? 'Open' : 'Connect'));
    const emptyStateText = isUsbTransfer
      ? 'Enter a local folder path (like a USB drive) and click Open.'
      : 'Please connect to a machine to browse and transfer programs.';
    this.shadowRoot.innerHTML = `
      <style>
        :host {
          display: flex;
          flex-direction: column;
          height: 100%;
          min-height: 0;
          overflow-y: auto;
          overflow-x: hidden;
          box-sizing: border-box;
          background: var(--vscode-editor-background, #1e1e1e);
          color: var(--vscode-editor-foreground, #d4d4d4);
          font-family: var(--vscode-font-family, sans-serif);
          padding: 10px;
          border-left: 1px solid var(--vscode-widget-border, #444);
        }
        .header {
          display: flex;
          gap: 10px;
          margin-bottom: 15px;
          align-items: center;
          flex-shrink: 0;
        }
        input {
          background: var(--vscode-input-background, #3c3c3c);
          color: var(--vscode-input-foreground, #cccccc);
          border: 1px solid var(--vscode-input-border, #3c3c3c);
          padding: 4px 8px;
        }
        button {
          background: var(--vscode-button-background, #0e639c);
          color: var(--vscode-button-foreground, #ffffff);
          border: none;
          padding: 4px 12px;
          cursor: pointer;
        }
        button:hover { background: var(--vscode-button-hoverBackground, #1177bb); }
        .list {
          display: flex;
          flex-direction: column;
          gap: 8px;
        }
        .prog-item {
          background: var(--vscode-editorWidget-background, #252526);
          border: 1px solid var(--vscode-widget-border, #444);
          padding: 8px;
          display: flex;
          justify-content: space-between;
          align-items: center;
        }
        .prog-info {
          display: flex;
          flex-direction: column;
        }
        .prog-num { font-weight: bold; color: var(--vscode-symbolIcon-keywordForeground, #007acc); }
        .pa-badge { background: #d7ba7d; color: #000; padding: 2px 6px; border-radius: 4px; font-size: 0.8em; margin-left: 5px; }
        .single-badge { border: 1px solid var(--vscode-button-background, #0e639c); color: var(--vscode-foreground, #d4d4d4); padding: 1px 5px; border-radius: 4px; font-size: 0.8em; margin-left: 5px; }
        .actions {
          display: flex;
          gap: 5px;
        }
        .actions button {
          font-size: 0.85em;
          background: var(--vscode-button-secondaryBackground, #3a3d41);
        }
        .actions button:hover { background: var(--vscode-button-secondaryHoverBackground, #45494e); }
        
        .download-panel {
          margin-top: 15px;
          padding-top: 15px;
          border-top: 1px solid var(--vscode-widget-border, #444);
        }
        .push-active-bar {
          display: flex;
          gap: 10px;
          margin-top: 10px;
          margin-bottom: 5px;
        }
      </style>
      
      <div class="header">
        <strong>Transfer</strong>
        ${isUsbTransfer ? '' : '<span id="ping-indicator" title="Ping Status" style="display:inline-block; width:10px; height:10px; border-radius:50%; background:gray; margin-left:8px; cursor:help;"></span>'}
        <input type="text" id="ip-address" value="${this.ipAddress === 'DEMO' && isUsbTransfer ? '' : this.ipAddress}" placeholder="${addressPlaceholder}" style="${isUsbTransfer ? 'flex: 1;' : ''}" title="${isUsbTransfer ? 'Enter full local path e.g. D:\\' : 'IP Address'}" />
        ${isUsbTransfer && (window as any).vscodeApi ? '<button id="browse-btn" title="Browse for folder">Browse</button>' : ''}
        <button id="connect-btn">${connectButtonLabel}</button>
      </div>

      ${this.isConnectedToCnc ? `
        <div class="list">
          <h3>${locationLabel}</h3>
          ${Array.from(this.cncPrograms.values()).sort((a,b)=>a.number-b.number).map(prog => `
            <div class="prog-item" draggable="true" data-drag-prog="${prog.number}" data-drag-pa="${prog.isPA ? 'true' : 'false'}">
              <div class="prog-info">
                <div>
                  <span class="prog-num">O${prog.number.toString().padStart(4, '0')}</span>
                  ${prog.isPA && multifileSupported
                    ? '<span class="pa-badge">PA Program</span>'
                    : `<span class="single-badge">${supportedPaths.filter(p => prog.paths[p as 1|2|3]).map(p => this.getChannelLabel(p)).join(', ')}</span>`
                  }
                </div>
                <small>${prog.comment}</small>
              </div>
              <div class="actions">
                <button class="btn-upl" data-path="${prog.isPA && multifileSupported ? 'PA' : supportedPaths.find(path => prog.paths[path as 1|2|3]) ?? 1}" data-prog="${prog.number}">Pull</button>
                ${supportedPaths.map(path => prog.paths[path as 1|2|3] ?
                  `${(window as any).vscodeApi ? `<button class="btn-cmp" data-path="${path}" data-prog="${prog.number}">Compare ${this.getChannelLabel(path)}</button>` : ''}` :
                  ''
                ).join('')}
              </div>
            </div>
          `).join('')}
          ${this.cncPrograms.size === 0 ? '<p>No programs found on machine.</p>' : ''}
        </div>

        <div class="download-panel">
          <div class="push-active-bar">
             <span style="align-self: center; font-size: 0.9em; flex: 1;"><strong>Push Open File:</strong></span>
             ${multifileSupported ? `<button class="btn-push-active" data-path="PA">PA</button>` : ''}
             ${supportedPaths.map(path => `<button class="btn-push-active" data-path="${path}">${this.getChannelLabel(path)}</button>`).join('')}
          </div>
        </div>
      ` : `
        <div class="list">
          <p>${emptyStateText}</p>
        </div>
      `}
    `;
  }

  private attachEventListeners() {
    this.shadowRoot?.getElementById('connect-btn')?.addEventListener('click', () => this.handleConnect());
    
    this.shadowRoot?.getElementById('browse-btn')?.addEventListener('click', () => {
      if ((window as any).vscodeApi) {
        (window as any).vscodeApi.postMessage({ type: 'SELECT_USB_DIRECTORY' });
      }
    });
    
    const ipInput = this.shadowRoot?.getElementById('ip-address') as HTMLInputElement;
    if (ipInput && String(this.transferProtocol).toLowerCase() !== 'usb') {
      ipInput.addEventListener('change', () => this.checkPing());
      ipInput.addEventListener('blur', () => this.checkPing());
    }
    
    if (this.isConnectedToCnc) {
      const uplButtons = this.shadowRoot?.querySelectorAll('.btn-upl');
      uplButtons?.forEach(btn => {
        btn.addEventListener('click', (e) => {
          const target = e.target as HTMLElement;
          const prog = parseInt(target.getAttribute('data-prog') || '0', 10);
          const path = target.getAttribute('data-path') || '1';
          if(prog) this.handleUpload(prog, path);
        });
      });

      const cmpButtons = this.shadowRoot?.querySelectorAll('.btn-cmp');
      cmpButtons?.forEach(btn => {
        btn.addEventListener('click', (e) => {
          const target = e.target as HTMLElement;
          const prog = parseInt(target.getAttribute('data-prog') || '0', 10);
          const path = target.getAttribute('data-path') || '1';
          if(prog) this.handleCompare(prog, path);
        });
      });

      const progItems = this.shadowRoot?.querySelectorAll('.prog-item');
      progItems?.forEach(item => {
        item.addEventListener('dragstart', (e) => {
          const dragEvent = e as DragEvent;
          if (dragEvent.dataTransfer) {
            const progNum = item.getAttribute('data-drag-prog') || '';
            const isPA = item.getAttribute('data-drag-pa') === 'true';
            
            // Set text payload so it can at least be dropped into text editors
            dragEvent.dataTransfer.setData('text/plain', `Transfer Program O${progNum.padStart(4, '0')} (${isPA ? 'PA' : 'Multi-path'})`);
            dragEvent.dataTransfer.effectAllowed = 'copy';
          }
        });
      });

      const pushActiveBtns = this.shadowRoot?.querySelectorAll('.btn-push-active');
      pushActiveBtns?.forEach(btn => {
        btn.addEventListener('click', (e) => {
           const target = e.target as HTMLElement;
           const path = target.getAttribute('data-path');
           if (path && (window as any).vscodeApi) {
               (window as any).vscodeApi.postMessage({
                   type: 'REQUEST_ACTIVE_PROGRAM_FOR_UPLOAD',
                   pathId: path
               });
           }
        });
      });

    }
  }

  private async uploadDroppedFile(content: string, targetPath: string | null) {
    if (!targetPath) return;

    try {
      this.loading = true;
      this.render();
      if (targetPath === 'PA') {
        if (String(this.transferProtocol).toLowerCase() === 'usb') {
          let cleanContent = content.trim();
          if (cleanContent.startsWith('%')) cleanContent = cleanContent.slice(1).trimStart();
          if (cleanContent.endsWith('%')) cleanContent = cleanContent.slice(0, -1).trimEnd();
          const finalContent = `\n${cleanContent}\n%`;
          await this.transferClient.downloadProgram(this.ipAddress, 0, finalContent, undefined, this.getPAFileExtension());
          alert('PA file pushed successfully as one .PA file.');
          return;
        }

        const tagRegex = /<\s*(O[A-Za-z0-9_]+)\.P(\d+)\s*>/gi;
        let match;
        let lastIndex = 0;
        let lastPath = -1;
        let lastProgramName = '';
        const pathContents: Record<number, { programName: string; content: string }> = {};

        while ((match = tagRegex.exec(content)) !== null) {
          if (lastPath !== -1) {
            pathContents[lastPath] = { programName: lastProgramName, content: content.substring(lastIndex, match.index).trim() };
          }
          lastProgramName = match[1];
          lastPath = parseInt(match[2], 10);
          lastIndex = tagRegex.lastIndex;
        }
        if (lastPath !== -1) {
          pathContents[lastPath] = { programName: lastProgramName, content: content.substring(lastIndex).trim() };
        }

        if (Object.keys(pathContents).length === 0) {
          alert('Could not find PA format tags (e.g., <O1234.P1>) in the file. Uploading aborted.');
          return;
        }

        const uploadedPaths: number[] = [];
        const errors: string[] = [];
        
        for (const [pStr, part] of Object.entries(pathContents)) {
          const p = parseInt(pStr, 10);
          
          let cleanContent = String(part.content).trim();
          if (cleanContent.startsWith('%')) cleanContent = cleanContent.slice(1).trimStart();
          if (cleanContent.endsWith('%')) cleanContent = cleanContent.slice(0, -1).trimEnd();
          if (part.programName && !new RegExp(`^${part.programName}\\b`, 'i').test(cleanContent)) {
            cleanContent = `${part.programName}\n${cleanContent}`.trimEnd();
          }
          
          // Transfer format: Must start with LF and end with % (no leading %)
          const finalContent = `\n${cleanContent}\n%`;
          
          try {
            await this.transferClient.downloadProgram(this.ipAddress, p, finalContent, undefined, this.getChannelFileExtension(p));
            uploadedPaths.push(p);
          } catch(e) {
            console.warn(`Failed to push to Path ${p}`, e);
            errors.push(`P${p}: ${e}`);
          }
        }
        
        if (errors.length > 0 && uploadedPaths.length === 0) {
          alert(`Failed to push PA file:\\n${errors.join('\\n')}`);
        } else if (errors.length > 0) {
          alert(`File pushed to Paths ${uploadedPaths.join(', ')}.\\nErrors:\\n${errors.join('\\n')}`);
        } else {
          alert(`PA File pushed to Paths ${uploadedPaths.join(', ')} successfully!`);
        }
      } else {
        const pathNo = parseInt(targetPath.replace('path', ''), 10);
        let cleanContent = content.trim();
        if (cleanContent.startsWith('%')) cleanContent = cleanContent.slice(1).trimStart();
        if (cleanContent.endsWith('%')) cleanContent = cleanContent.slice(0, -1).trimEnd();
        const finalContent = `\n${cleanContent}\n%`;
        
        await this.transferClient.downloadProgram(this.ipAddress, pathNo, finalContent, undefined, this.getChannelFileExtension(pathNo));
        alert(`File pushed to Path ${pathNo} successfully!`);
      }
    } catch(e) {
      alert("Push failed: " + e);
    } finally {
      await this.fetchPrograms();
      this.loading = false;
      this.render();
      this.attachEventListeners();
    }
  }

}

customElements.define('nc-transfer-panel', NCTransferPanel);
